import { useState, useMemo } from 'react'
import {
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CheckCircle,
  CalendarClock,
  ClipboardCheck,
  Loader2,
  BookOpen,
  Lightbulb,
  Palette,
  Gamepad2,
  Dumbbell,
  Music,
  Globe,
  PartyPopper,
  Palmtree,
  type LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn, darkenColor, lightenColor } from '@/lib/utils'
import { useAuth } from '@/features/auth/hooks/use-auth'
import { useEtudiantByUserId } from '@/features/etudiants/hooks/use-etudiants'
import { useAgendaComplet } from '@/features/etudiants/hooks/use-agenda'
import { usePeriodesEtudiant } from '@/features/etudiants/hooks/use-calendrier'
import { useTypesActivite } from '@/features/types-activite/hooks/use-types-activite'
import { useMyEvents } from '@/features/events/hooks/use-events'
import { useDarkMode } from '@/shared/hooks/use-dark-mode'
import { BottomNav } from '../components/bottom-nav'
import type { TacheAgenda, TacheLangue, AgendaCompletDto } from '@/shared/types/agenda'
import type { ActivityResponseDto } from '@/shared/types/activite'
import type { PeriodeEtudiantDto } from '@/shared/types/calendrier'

// ── Helpers ─────────────────────────────────────────────────

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function formatDateApi(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function getWeekType(
  date: Date,
  periodes: PeriodeEtudiantDto[] | undefined,
): 'A' | 'B' | 'VACANCES' | 'RELAX' | null {
  if (!periodes) return null
  const dateStr = formatDateApi(date)
  for (const p of periodes) {
    if (!p.datesPeriodes) continue
    for (const dp of p.datesPeriodes) {
      if (dateStr >= dp.dateDebut && dateStr < dp.dateFin) {
        if (p.typePeriode === 'RELAX') return 'RELAX'
        if (p.typePeriode === 'SEMAINE_A') return 'A'
        if (p.typePeriode === 'SEMAINE_B') return 'B'
        if (p.typePeriode === 'VACANCES') return 'VACANCES'
      }
    }
  }
  return null
}

function buildMonthGrid(year: number, month: number): Date[] {
  const firstDay = new Date(year, month, 1)
  let startDow = firstDay.getDay() - 1
  if (startDow < 0) startDow = 6

  const cells: Date[] = []
  const startDate = new Date(year, month, 1 - startDow)
  for (let i = 0; i < 42; i++) {
    const d = new Date(startDate)
    d.setDate(startDate.getDate() + i)
    cells.push(d)
  }

  // Tronquer la dernière ligne si tous les jours sont hors mois
  const lastRow = cells.slice(35)
  if (lastRow.every((d) => d.getMonth() !== month)) {
    return cells.slice(0, 35)
  }
  return cells
}

// ── Section building (same logic as dashboard) ──────────────

const SECTION_ICON_MAP: Record<string, LucideIcon> = {
  rev: BookOpen,
  révis: BookOpen,
  revis: BookOpen,
  culture: Lightbulb,
  loisir: Gamepad2,
  sport: Dumbbell,
  musique: Music,
  art: Palette,
  langue: Globe,
}

function getSectionIcon(code: string, libelle: string): LucideIcon {
  const search = `${code} ${libelle}`.toLowerCase()
  for (const [keyword, icon] of Object.entries(SECTION_ICON_MAP)) {
    if (search.includes(keyword)) return icon
  }
  return BookOpen
}

interface DaySection {
  key: string
  title: string
  color: string
  icon: LucideIcon
  timeSlots: string[]
  taches: TacheAgenda[]
  tachesLangue: TacheLangue[]
  activites: ActivityResponseDto[]
}

function buildSections(agenda: AgendaCompletDto, revisionColor: string | undefined, t: (key: string) => string): DaySection[] {
  const sections: DaySection[] = []

  const groups = new Map<number, { type: ActivityResponseDto['typeActivite']; items: ActivityResponseDto[] }>()
  for (const act of agenda.activites) {
    const tid = act.typeActivite.id
    if (!groups.has(tid)) groups.set(tid, { type: act.typeActivite, items: [] })
    groups.get(tid)!.items.push(act)
  }

  let revTypeId: number | null = null
  for (const [tid, g] of groups) {
    const c = g.type.code.toLowerCase()
    const l = g.type.libelle.toLowerCase()
    if (c.includes('rev') || l.includes('révis') || l.includes('revis')) {
      revTypeId = tid
      break
    }
  }

  // Séparer tâches quotidiennes (planning) et tâches événements
  const planningTasks = agenda.taches.filter((t) => t.isFromPlanning)
  const controleTasks = agenda.taches.filter((t) => !t.isFromPlanning && t.eventOrigin === 'CONTROLE')
  const vacancesTasks = agenda.taches.filter((t) => !t.isFromPlanning && t.eventOrigin === 'VACANCES')
  const otherEventTasks = agenda.taches.filter((t) => !t.isFromPlanning && !t.eventOrigin)

  // Révisions quotidiennes (issues du planning)
  if (planningTasks.length > 0 || otherEventTasks.length > 0) {
    const rev = revTypeId !== null ? groups.get(revTypeId) : undefined
    sections.push({
      key: 'revisions',
      title: rev?.type.libelle ?? t('mobile.dashboard.revisions'),
      color: rev?.type.couleur ?? revisionColor ?? '#FF6B6B',
      icon: rev ? getSectionIcon(rev.type.code, rev.type.libelle) : BookOpen,
      timeSlots: rev?.items.map((a) => `${a.startTime} - ${a.endTime}`) ?? [],
      taches: [...planningTasks, ...otherEventTasks].sort((a, b) => a.action.id - b.action.id),
      tachesLangue: [],
      activites: [],
    })
  }

  // Préparation contrôle
  if (controleTasks.length > 0) {
    sections.push({
      key: 'revisions-controle',
      title: t('mobile.dashboard.testPrep'),
      color: '#F59E0B',
      icon: ClipboardCheck,
      timeSlots: [],
      taches: [...controleTasks].sort((a, b) => a.action.id - b.action.id),
      tachesLangue: [],
      activites: [],
    })
  }

  // Révisions de vacances
  if (vacancesTasks.length > 0) {
    sections.push({
      key: 'revisions-vacances',
      title: t('mobile.dashboard.vacationRevisions'),
      color: '#22C55E',
      icon: Palmtree,
      timeSlots: [],
      taches: [...vacancesTasks].sort((a, b) => a.action.id - b.action.id),
      tachesLangue: [],
      activites: [],
    })
  }

  // Langues section
  if (agenda.tachesLangue.length > 0) {
    sections.push({
      key: 'langues',
      title: t('mobile.dashboard.languages'),
      color: '#22C55E',
      icon: Globe,
      timeSlots: [],
      taches: [],
      tachesLangue: agenda.tachesLangue,
      activites: [],
    })
  }

  // Tâches reportées (visible uniquement pour les jours != aujourd'hui)
  if (agenda.tachesReportees && agenda.tachesReportees.length > 0) {
    sections.push({
      key: 'taches-reportees',
      title: t('mobile.dashboard.postponedTasks'),
      color: '#F59E0B',
      icon: CalendarClock,
      timeSlots: [],
      taches: agenda.tachesReportees,
      tachesLangue: [],
      activites: [],
    })
  }

  for (const [tid, g] of groups) {
    if (tid === revTypeId && (planningTasks.length > 0 || otherEventTasks.length > 0)) continue
    sections.push({
      key: `type-${tid}`,
      title: g.type.libelle,
      color: g.type.couleur ?? '#6B7280',
      icon: getSectionIcon(g.type.code, g.type.libelle),
      timeSlots: [],
      taches: [],
      tachesLangue: [],
      activites: g.items,
    })
  }

  return sections
}

// ── Section Header ───────────────────────────────────────────

function SectionHeader({
  title, color, icon: Icon, timeSlots, collapsed, onToggle, taskCount, isDark,
}: {
  title: string; color: string; icon: LucideIcon; timeSlots: string[]
  collapsed: boolean; onToggle: () => void; taskCount: number; isDark: boolean
}) {
  const dark = isDark ? lightenColor(color) : darkenColor(color)
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full flex-wrap items-center gap-2 px-1 pb-2.5"
    >
      <div
        className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full"
        style={{ backgroundColor: color + '20' }}
      >
        <Icon className="h-3.5 w-3.5" style={{ color: dark }} />
      </div>
      <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: dark }}>
        {title}
      </h4>
      {timeSlots.map((slot, i) => (
        <span
          key={i}
          className="rounded-full px-2.5 py-0.5 text-[11px] font-medium"
          style={{ backgroundColor: color + '15', color: dark }}
        >
          {slot}
        </span>
      ))}
      <span className="ml-auto flex items-center gap-1">
        {collapsed && (
          <span className="rounded-full px-2 py-0.5 text-[10px] font-bold" style={{ backgroundColor: color + '20', color: dark }}>
            {taskCount}
          </span>
        )}
        <ChevronDown
          className="h-4 w-4 transition-transform"
          style={{ color: dark, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
        />
      </span>
    </button>
  )
}

// ── Relax Day Card ───────────────────────────────────────────

function RelaxDayCard({ libelle }: { libelle: string | null }) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col items-center rounded-2xl bg-gradient-to-b from-amber-50/80 to-white px-6 py-10">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-amber-100">
        <PartyPopper className="h-8 w-8 text-amber-500" strokeWidth={1.8} />
      </div>
      <h2 className="mt-4 text-lg font-bold text-neutral-800">
        {libelle || t('mobile.dashboard.relaxDay')}
      </h2>
      <p className="mt-2 max-w-[260px] text-center text-sm leading-relaxed text-neutral-400">
        {t('mobile.dashboard.relaxDayMessage')}
      </p>
    </div>
  )
}

// ── Main Page ───────────────────────────────────────────────

export function CalendarMobilePage() {
  const { user } = useAuth()
  const { data: etudiant } = useEtudiantByUserId(user?.id)
  const { isDark } = useDarkMode()
  const { t } = useTranslation()
  const monthNames = t('mobile.months', { returnObjects: true }) as string[]
  const weekdayHeaders = t('mobile.weekdayHeaders', { returnObjects: true }) as string[]

  const [selectedDate, setSelectedDate] = useState(() => new Date())
  const [viewMonth, setViewMonth] = useState(() => new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1))

  const { data: agenda, isLoading: agendaLoading } = useAgendaComplet(etudiant?.id, selectedDate)
  const { data: periodes } = usePeriodesEtudiant(etudiant?.id)
  const { data: typesActivite } = useTypesActivite()
  const { data: myEvents } = useMyEvents()

  const revisionColor = useMemo(() => {
    if (!typesActivite) return undefined
    const rev = typesActivite.find((t) => t.code.toLowerCase().includes('rev'))
    return rev?.couleur
  }, [typesActivite])

  const relaxDates = useMemo(() => {
    if (!myEvents) return new Set<string>()
    const dates = new Set<string>()
    for (const e of myEvents) {
      if (e.type === 'RELAX') {
        const start = new Date(e.dateDebut + 'T00:00:00')
        const end = e.dateFin ? new Date(e.dateFin + 'T00:00:00') : start
        const current = new Date(start)
        while (current <= end) {
          dates.add(formatDateApi(current))
          current.setDate(current.getDate() + 1)
        }
      }
    }
    return dates
  }, [myEvents])

  const controleMap = useMemo(() => {
    if (!myEvents) return new Map<string, { matiereNom: string | null; matiereCouleur: string | null }>()
    const map = new Map<string, { matiereNom: string | null; matiereCouleur: string | null }>()
    for (const e of myEvents) {
      if (e.type === 'CONTROLE') {
        map.set(e.dateDebut, { matiereNom: e.matiereNom, matiereCouleur: e.matiereCouleur })
      }
    }
    return map
  }, [myEvents])

  const vacancesRevisionDates = useMemo(() => {
    if (!myEvents) return new Set<string>()
    const dates = new Set<string>()
    for (const e of myEvents) {
      if (e.type === 'VACANCES') {
        const start = new Date(e.dateDebut + 'T00:00:00')
        const end = e.dateFin ? new Date(e.dateFin + 'T00:00:00') : start
        const current = new Date(start)
        while (current <= end) {
          dates.add(formatDateApi(current))
          current.setDate(current.getDate() + 1)
        }
      }
    }
    return dates
  }, [myEvents])

  const relaxEvent = useMemo(() => {
    if (!myEvents) return undefined
    const dateStr = formatDateApi(selectedDate)
    return myEvents.find((e) => {
      if (e.type !== 'RELAX') return false
      if (e.dateFin) return dateStr >= e.dateDebut && dateStr <= e.dateFin
      return e.dateDebut === dateStr
    })
  }, [myEvents, selectedDate])

  const controleEvent = useMemo(() => {
    if (!myEvents) return undefined
    const dateStr = formatDateApi(selectedDate)
    return myEvents.find((e) => e.type === 'CONTROLE' && e.dateDebut === dateStr)
  }, [myEvents, selectedDate])

  const cells = useMemo(
    () => buildMonthGrid(viewMonth.getFullYear(), viewMonth.getMonth()),
    [viewMonth],
  )

  const today = useMemo(() => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    return now
  }, [])

  const hasABWeeks = periodes?.some((p) => p.typePeriode === 'SEMAINE_A' || p.typePeriode === 'SEMAINE_B')
  const hasVacances = periodes?.some((p) => p.typePeriode === 'VACANCES')
  const hasAdminRelax = periodes?.some((p) => p.typePeriode === 'RELAX')
  const hasRelaxDays = relaxDates.size > 0 || hasAdminRelax
  const hasControles = controleMap.size > 0
  const hasVacancesRevision = vacancesRevisionDates.size > 0

  const goMonth = (delta: number) => {
    setViewMonth((prev) => new Date(prev.getFullYear(), prev.getMonth() + delta, 1))
  }

  const handleSelectDay = (d: Date) => {
    setSelectedDate(d)
    if (d.getMonth() !== viewMonth.getMonth() || d.getFullYear() !== viewMonth.getFullYear()) {
      setViewMonth(new Date(d.getFullYear(), d.getMonth(), 1))
    }
  }

  const sections = useMemo(() => (agenda ? buildSections(agenda, revisionColor, t) : []), [agenda, revisionColor, t])

  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set())
  const toggleSection = (key: string) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev)
      if (next.has(key)) next.delete(key)
      else next.add(key)
      return next
    })
  }

  return (
    <div className="flex min-h-[100dvh] flex-col bg-gradient-to-b from-primary/5 via-primary/[0.02] to-white">
      {/* Sticky header */}
      <div className="sticky top-0 z-40 bg-white/95 pt-3 backdrop-blur-md">
        {/* Month navigation */}
        <div className="flex items-center justify-between px-5 pb-1">
          <button onClick={() => goMonth(-1)} className="rounded-full p-1.5 active:bg-neutral-100">
            <ChevronLeft className="h-5 w-5 text-neutral-500" />
          </button>
          <h2 className="text-sm font-bold text-neutral-800">
            {monthNames[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </h2>
          <button onClick={() => goMonth(1)} className="rounded-full p-1.5 active:bg-neutral-100">
            <ChevronRight className="h-5 w-5 text-neutral-500" />
          </button>
        </div>

        {/* Weekday headers */}
        <div className="grid grid-cols-7 px-3">
          {weekdayHeaders.map((l, i) => (
            <div key={i} className="py-0.5 text-center text-[10px] font-bold uppercase tracking-wider text-neutral-400">
              {l}
            </div>
          ))}
        </div>

        {/* Calendar grid — compact */}
        <div className="grid grid-cols-7 gap-px px-3 pb-2">
          {cells.map((date, i) => {
            const inMonth = date.getMonth() === viewMonth.getMonth()
            const isDayToday = isSameDay(date, today)
            const isSelected = isSameDay(date, selectedDate)
            const weekType = getWeekType(date, periodes)
            const dateStr = formatDateApi(date)
            const isRelaxDay = relaxDates.has(dateStr) || weekType === 'RELAX'
            const controleInfo = controleMap.get(dateStr)
            const isVacancesRevision = vacancesRevisionDates.has(dateStr)

            let bgStyle: React.CSSProperties = {}
            if (isRelaxDay) bgStyle = { backgroundColor: 'rgba(168,85,247,0.22)' }
            else if (weekType === 'A') bgStyle = { backgroundColor: 'rgba(33,150,243,0.08)' }
            else if (weekType === 'B') bgStyle = { backgroundColor: 'rgba(244,67,54,0.08)' }
            else if (weekType === 'VACANCES' && isVacancesRevision) bgStyle = { backgroundColor: 'rgba(234,179,8,0.18)' }
            else if (weekType === 'VACANCES') bgStyle = { backgroundColor: 'rgba(34,197,94,0.10)' }

            return (
              <button
                key={i}
                onClick={() => handleSelectDay(date)}
                className={cn(
                  'relative flex h-9 items-center justify-center rounded-lg transition-all',
                  !inMonth && 'opacity-25',
                  isSelected && 'ring-2 ring-primary',
                )}
                style={isSelected ? { backgroundColor: 'rgba(121,48,93,0.12)' } : bgStyle}
              >
                <span
                  className={cn(
                    'flex h-6 w-6 items-center justify-center rounded-full text-xs',
                    isDayToday && !isSelected && 'bg-primary/15 font-bold text-primary',
                    isSelected && 'bg-primary font-bold text-white',
                    !isDayToday && !isSelected && (inMonth ? 'font-medium text-neutral-700' : 'text-neutral-400'),
                  )}
                >
                  {date.getDate()}
                </span>
                {controleInfo && inMonth && (
                  <span
                    className="absolute bottom-0 right-0 h-0 w-0"
                    style={{
                      borderLeft: '12px solid transparent',
                      borderBottom: '12px solid #EF4444',
                      borderBottomRightRadius: '8px',
                    }}
                  />
                )}
              </button>
            )
          })}
        </div>

        {/* Legend */}
        {(hasABWeeks || hasVacances || hasRelaxDays || hasControles) && (
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 px-5 pb-2">
            {hasABWeeks && (
              <>
                <div className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: 'rgba(33,150,243,0.4)' }} />
                  <span className="text-[10px] font-semibold text-neutral-400">{t('mobile.calendar.weekA')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: 'rgba(244,67,54,0.4)' }} />
                  <span className="text-[10px] font-semibold text-neutral-400">{t('mobile.calendar.weekB')}</span>
                </div>
              </>
            )}
            {hasVacances && (
              <div className="flex items-center gap-1">
                <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: 'rgba(34,197,94,0.4)' }} />
                <span className="text-[10px] font-semibold text-neutral-400">{t('mobile.calendar.vacation')}</span>
              </div>
            )}
            {hasVacancesRevision && (
              <div className="flex items-center gap-1">
                <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: 'rgba(234,179,8,0.6)' }} />
                <span className="text-[10px] font-semibold text-neutral-400">{t('mobile.calendar.vacationRevision')}</span>
              </div>
            )}
            {hasRelaxDays && (
              <div className="flex items-center gap-1">
                <div className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: 'rgba(168,85,247,0.5)' }} />
                <span className="text-[10px] font-semibold text-neutral-400">{t('mobile.calendar.relax')}</span>
              </div>
            )}
            {hasControles && (
              <div className="flex items-center gap-1">
                <div className="relative h-2.5 w-2.5">
                  <span
                    className="absolute bottom-0 right-0 h-0 w-0"
                    style={{ borderLeft: '6px solid transparent', borderBottom: '6px solid #EF4444', borderBottomRightRadius: '2px' }}
                  />
                </div>
                <span className="text-[10px] font-semibold text-neutral-400">{t('mobile.calendar.test')}</span>
              </div>
            )}
          </div>
        )}

        {/* Divider */}
        <div className="border-t border-neutral-100" />
      </div>

      {/* Selected day agenda */}
      <div className="flex-1 px-4 pb-24 pt-3">
        {agendaLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-primary/50" />
          </div>
        ) : relaxEvent ? (
          <RelaxDayCard libelle={relaxEvent.libelle} />
        ) : sections.length === 0 && !controleEvent ? (
          <div className="flex flex-col items-center py-10">
            <span className="text-2xl">📋</span>
            <p className="mt-1 text-xs text-neutral-400">{t('mobile.calendar.emptyDay')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {controleEvent && (
              <div
                className="overflow-hidden rounded-2xl"
                style={{ backgroundColor: (controleEvent.matiereCouleur || '#EF4444') + '12' }}
              >
                <div className="flex items-center gap-3 px-4 py-3.5">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl"
                    style={{ backgroundColor: (controleEvent.matiereCouleur || '#EF4444') + '25' }}
                  >
                    <ClipboardCheck className="h-5 w-5" style={{ color: controleEvent.matiereCouleur || '#EF4444' }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-bold text-neutral-800">
                      {controleEvent.matiereNom ? t('mobile.dashboard.testOf', { subject: controleEvent.matiereNom }) : t('mobile.dashboard.test')}
                    </p>
                    <p className="mt-0.5 text-xs text-neutral-400">
                      {t('mobile.dashboard.testEncouragement')}
                    </p>
                  </div>
                </div>
              </div>
            )}
            {sections.map((section) => {
              const isCollapsed = collapsedSections.has(section.key)
              const taskCount = section.taches.length + section.tachesLangue.length + section.activites.length
              return (
                <div
                  key={section.key}
                  className="rounded-2xl p-3"
                  style={{ backgroundColor: section.color + '0D' }}
                >
                  <SectionHeader
                    title={section.title}
                    color={section.color}
                    icon={section.icon}
                    timeSlots={section.timeSlots}
                    collapsed={isCollapsed}
                    onToggle={() => toggleSection(section.key)}
                    taskCount={taskCount}
                    isDark={isDark}
                  />

                  {!isCollapsed && (
                    <div className="space-y-2">
                      {/* Study tasks */}
                      {section.taches.map((tache) => {
                        const accentColor = tache.matieres[0]?.matiereCouleur || section.color
                        return (
                          <div
                            key={`t-${tache.id}`}
                            className={cn(
                              'relative overflow-hidden rounded-xl bg-white',
                              tache.termine ? 'opacity-70' : 'shadow-sm',
                            )}
                          >
                            <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl" style={{ backgroundColor: tache.termine ? '#10b981' : accentColor }} />
                            <div className="p-3 pl-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className={cn('text-sm font-bold', tache.termine ? 'text-neutral-400' : 'text-neutral-900')}>
                                  {tache.action.libelle}
                                </span>
                                {tache.termine ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    {t('mobile.dashboard.completed')}
                                  </span>
                                ) : (
                                  <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
                                    {tache.action.dureeMinutes} min
                                  </span>
                                )}
                              </div>
                              <div className="mt-1.5 flex flex-wrap gap-1.5">
                                {tache.matieres.map((m, j) => (
                                  <span
                                    key={j}
                                    className="rounded-lg px-2.5 py-0.5 text-xs font-semibold"
                                    style={{ backgroundColor: (m.matiereCouleur || section.color) + '18', color: m.matiereCouleur || section.color }}
                                  >
                                    {m.matiereNom}
                                  </span>
                                ))}
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      {/* Language tasks */}
                      {section.tachesLangue.map((tacheL) => {
                        const c = tacheL.matiereCouleur || section.color
                        return (
                          <div
                            key={`l-${tacheL.id}`}
                            className={cn(
                              'relative overflow-hidden rounded-xl bg-white',
                              tacheL.termine ? 'opacity-70' : 'shadow-sm',
                            )}
                          >
                            <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl" style={{ backgroundColor: tacheL.termine ? '#10b981' : c }} />
                            <div className="p-3 pl-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className={cn('text-sm font-bold', tacheL.termine ? 'text-neutral-400' : 'text-neutral-900')}>
                                  {tacheL.libelle}
                                </span>
                                {tacheL.termine && (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    {t('mobile.dashboard.completed')}
                                  </span>
                                )}
                              </div>
                              <div className="mt-1.5">
                                <span
                                  className="rounded-lg px-2.5 py-0.5 text-xs font-semibold"
                                  style={{ backgroundColor: c + '18', color: c }}
                                >
                                  {tacheL.matiereNom}
                                </span>
                              </div>
                            </div>
                          </div>
                        )
                      })}

                      {/* Activities */}
                      {section.activites.map((a) => {
                        const c = a.typeActivite.couleur ?? '#6B7280'
                        const totalSec = a.dureeMinutes * 60
                        const started = a.timerSeconds !== null && a.timerSeconds < totalSec
                        const progress = started ? Math.round(((totalSec - a.timerSeconds!) / totalSec) * 100) : 0
                        return (
                          <div key={`a-${a.id}`} className={cn('relative overflow-hidden rounded-xl bg-white', a.termine ? 'opacity-70' : 'shadow-sm')}>
                            <div className="absolute inset-y-0 left-0 w-1 rounded-l-xl" style={{ backgroundColor: a.termine ? '#10b981' : c }} />
                            <div className="p-3 pl-4">
                              <div className="flex items-center justify-between gap-2">
                                <span className={cn('text-sm font-bold', a.termine ? 'text-neutral-400' : 'text-neutral-900')}>{a.title}</span>
                                {a.termine ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-500">
                                    <CheckCircle className="h-3.5 w-3.5" />
                                    {t('mobile.dashboard.completed')}
                                  </span>
                                ) : (
                                  <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
                                    {a.dureeMinutes} min
                                  </span>
                                )}
                              </div>
                              {!a.termine && started && (
                                <div className="mt-2.5 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                                  <div className="h-full rounded-full transition-all" style={{ width: `${progress}%`, backgroundColor: c }} />
                                </div>
                              )}
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <BottomNav />
    </div>
  )
}
